import React, { useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';
import ProtectedImage from './ProtectedImage';
import { useTheme } from '@mui/material/styles';
import { getLogoPath as getBrandingLogoRasterPath } from '../../utils/logoUtils';
import { logger } from "../../utils/logger";
// import { logger } from "../../utils/logger";
export interface LogoProps extends Omit<BoxProps, 'component'> {
    variant?: 'full' | 'icon' | 'text' | 'eagle';
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'mobile';
    transparent?: boolean;
    noBuffer?: boolean;
    wide?: boolean;
    className?: string;
    style?: React.CSSProperties;
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'auto' | 'high' | 'low';
}

/**
 * Text-only variant uses SVG. `full` uses horizontal lockups (optionally wide).
 * `icon` + transparent + noBuffer uses compact transparent marks from `/icons/`
 * (`icon-*-128x128-transparent.png`); never maskable PWA icons with opaque backgrounds.
 */
const getTextLogoSvgAssetPath = (transparent: boolean, wide: boolean, mode: 'light' | 'dark'): string => {
    const base = '/images/branding';
    if (mode === 'dark') {
        const dir = `${base}/dark/svg`;
        return transparent ? `${dir}/apt-print-dark-transparent.svg` : `${dir}/apt-print-dark.svg`;
    }
    const folder = wide ? `${base}/wide/svg` : `${base}/standard/svg`;
    return transparent
        ? `${folder}/${wide ? 'apt-print-wide-transparent.svg' : 'apt-print-transparent.svg'}`
        : `${folder}/${wide ? 'apt-print-wide.svg' : 'apt-print.svg'}`;
};

const Logo: React.FC<LogoProps> = ({ variant = 'full', size = 'medium', transparent = false, noBuffer = false, wide = false, className, style, loading = 'lazy', fetchPriority = 'auto', ...boxProps }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const sizeMap = {
        small: { width: 120, height: 40 },
        medium: { width: 180, height: 60 },
        large: { width: 240, height: 80 },
        xlarge: { width: 320, height: 107 },
        mobile: { width: 120, height: 40 },
    };
    /** Target raster height for icon mark (width follows intrinsic aspect in layout). */
    const iconMarkTargetHeightPx: Record<NonNullable<LogoProps['size']>, number> = {
        mobile: 32,
        small: 40,
        medium: 48,
        large: 56,
        xlarge: 64,
    };
    const logoPath = useMemo(() => {
        // Text-only stays SVG. Full transparent lockups must use no-buffer PNGs:
        // Fabric SVG exports are 1280×1024 canvases, so object-fit into the header's
        // ~6.5:1 box shrinks the artwork to a speck.
        if (variant === 'text') {
            return getTextLogoSvgAssetPath(transparent, wide, mode);
        }
        const wideForRaster = variant === 'icon' ? false : wide;
        return getBrandingLogoRasterPath({
            variant,
            theme: mode === 'dark' ? 'dark' : 'light',
            format: 'png',
            transparent,
            noBuffer,
            wide: wideForRaster,
        });
    }, [variant, transparent, wide, mode, noBuffer]);
    const dimensions = useMemo(() => {
        if (variant === 'icon') {
            // Compact UI icons are square 128×128 (see logoUtils); not the 1280×819 PWA mark.
            const h = iconMarkTargetHeightPx[size ?? 'medium'] ?? 40;
            return { width: h, height: h };
        }
        return sizeMap[size];
    }, [variant, size]);
    const intrinsicDimensions = useMemo(() => {
        if (variant === 'icon') {
            return { width: 128, height: 128 };
        }
        if (variant === 'eagle') {
            return { width: 512, height: 512 };
        }
        if (variant === 'full') {
            return noBuffer
                ? { width: 1280, height: 197 }
                : { width: 1280, height: 1024 };
        }
        return dimensions;
    }, [variant, noBuffer, dimensions]);
    const isIconMark = variant === 'icon';
    // Use display size for HTML width/height so the browser reserves the correct box
    // (intrinsic 1280×197 attrs overstated aspect/download expectations for ~180–320 CSS widths).
    return (<ProtectedImage
      src={logoPath}
      alt={`American Police Training ${variant} logo`}
      loading={loading}
      fetchPriority={fetchPriority}
      width={dimensions.width}
      height={dimensions.height}
      brandingResponsive={variant === 'full'}
      sizes={variant === 'full' ? '(max-width: 899px) 48px, 364px' : undefined}
      sx={{
            ...(isIconMark
                ? {
                    width: dimensions.width,
                    height: dimensions.height,
                    maxHeight: dimensions.height,
                    maxWidth: '100%',
                    aspectRatio: '1 / 1',
                    minWidth: 0,
                    minHeight: 0,
                    objectFit: 'contain',
                }
                : {
                    width: dimensions.width,
                    height: dimensions.height,
                    maxWidth: dimensions.width,
                    maxHeight: dimensions.height,
                    minWidth: dimensions.width,
                    minHeight: dimensions.height,
                    objectFit: 'contain',
                    aspectRatio: `${intrinsicDimensions.width} / ${intrinsicDimensions.height}`,
                }),
            display: 'block',
            ...style,
        }} className={className} onError={(e) => {
            const img = e.currentTarget;
            if (!img.dataset.logoFallbackApplied) {
                img.dataset.logoFallbackApplied = 'true';
                // Drop broken image-proxy srcset so the browser does not prefer 404 candidates.
                img.removeAttribute('srcset');
                img.removeAttribute('sizes');
                try {
                    if (logoPath.startsWith('http')) {
                        img.src = new URL(logoPath, window.location.href).pathname;
                    }
                    else {
                        img.src = logoPath;
                    }
                }
                catch {
                    img.src = logoPath;
                }
                return;
            }
            logger.error('Logo failed to load:', logoPath, { "arg0": e });
        }} onLoad={() => {
            logger.debug('Logo loaded successfully:', { "arg0": logoPath });
        }} {...boxProps}/>);
};
export default Logo;
